import React, { useState, forwardRef } from 'react';
import { CopyIcon, CheckIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  monospace?: boolean;
  copyable?: boolean;
  revealable?: boolean;
}
export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  hint,
  monospace,
  copyable,
  revealable,
  className = '',
  type = 'text',
  value,
  ...props
}, ref) => {
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const handleCopy = async () => {
    if (value) {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  const inputType = revealable ? revealed ? 'text' : 'password' : type;
  return <div className="w-full" data-id="element-621">
        {label && <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" data-id="element-622">
            {label}
          </label>}
        <div className="relative" data-id="element-623">
          <input ref={ref} type={inputType} value={value} className={`
              w-full px-3 py-2 text-sm rounded-md border
              bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
              disabled:bg-slate-50 disabled:text-slate-500 dark:disabled:bg-slate-800 dark:disabled:text-slate-500
              ${error ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-700'}
              ${monospace ? 'font-mono text-xs' : ''}
              ${copyable || revealable ? 'pr-20' : ''}
              ${className}
            `} {...props} data-id="element-624" />
          {(copyable || revealable) && <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1" data-id="element-625">
              {revealable && <button type="button" onClick={() => setRevealed(!revealed)} className="p-1 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors" aria-label={revealed ? 'Hide value' : 'Reveal value'} data-id="element-626">
                  {revealed ? <EyeOffIcon className="h-4 w-4" data-id="element-627" /> : <EyeIcon className="h-4 w-4" data-id="element-628" />}
                </button>}
              {copyable && <button type="button" onClick={handleCopy} className="p-1 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors" aria-label="Copy to clipboard" data-id="element-629">
                  {copied ? <CheckIcon className="h-4 w-4 text-green-600 dark:text-green-500" data-id="element-630" /> : <CopyIcon className="h-4 w-4" data-id="element-631" />}
                </button>}
            </div>}
        </div>
        {hint && !error && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400" data-id="element-632">
            {hint}
          </p>}
        {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400" data-id="element-633">{error}</p>}
      </div>;
});
Input.displayName = 'Input';